import { useState, useContext, useCallback, useEffect } from "react";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import AuthContext from "../../../../utils/secure-route/AuthContext";
import "./Settings.css";
import useSwal from "../../../common/ReusableComponent/useSwal";
import { ToastContainer } from "react-toastify";
import expenseTypeIcon from "../../../../assets/Image/expensetypeicon.png";
import { useToastHandler } from "../../../common/ToastHandler";
import ReusableTable from "../../../common/ReusableComponent/reusableTable/ReusableTable";
import PaginatedSearchSelect from "../../../common/ReusableComponent/PaginatedSearchSelect/PaginatedSearchSelect";
import ReusableModal from "../ManageSettings/ReusableModal";
import selectIcon from "../../../../assets/Image/SelectClear.png";
import { CustomButton } from "../../../common/ReusableComponent/CustomButton";
import ChipList from "../../../common/ReusableComponent/ChipList";
import { Trash2 } from "lucide-react";
import { fetchAllPaginatedOptions, getBulkResultMessage } from "./settingsConfigUtils";

function ManageExpenseType({ embedded = false }) {
  const [, setExpenseType] = useState("");
  const { user } = useContext(AuthContext);
  const storeId = user.store;
  const { Post, Delete, GetPaginatedData } = useApi();
  const [loading, setLoading] = useState(true);
  const { handleToast } = useToastHandler();
  const { confirmDelete } = useSwal();
  const [RefreshTrigger, setRefreshTrigger] = useState(0);
  const [expenseTypeRows, setExpenseTypeRows] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedExpenseList, setSelectedExpenseList] = useState([]);

  const fetchExpenseTypeData = useCallback(async ({ page = 1, searchTerm = "", sort = "" } = {}) => {
    const trimmedSearchTerm = searchTerm.trim();
    try {
      setLoading(true);
      const paginatedResponse = embedded && !trimmedSearchTerm && !sort
        ? null
        : await GetPaginatedData("vendorDepartmentServiceData", {
            page,
            search: trimmedSearchTerm || undefined,
            queryParams: {
              ...(sort && { sort }),
              store: storeId,
            },
          });
      const allRecords = paginatedResponse
        ? paginatedResponse.data || []
        : await fetchAllPaginatedOptions(GetPaginatedData, "vendorDepartmentServiceData");
      const results = allRecords.map(item => ({
        id: item.id,
        title: item.all_expense_type_detail?.title || item.custom_title || "",
        description: item.all_expense_type_detail?.description || "",
        store: item.store,
        all_expense_type: item.all_expense_type,
        custom_type: item.custom_type,
        created_at: item.created_at,
        updated_at: item.updated_at
      })).filter(item => item.all_expense_type !== null || item.custom_type === "expense_type");

      setExpenseTypeRows(results);

      const totalPages = embedded
        ? 1
        : Math.max(1, Math.ceil((paginatedResponse?.totalCount || results.length) / 10));
      return { data: results, totalPages };
    } catch (error) {
      console.error("Error fetching expense types:", error);
      handleToast({
        type: "ERROR",
        message: "Error fetching expense types.",
      });
      return { data: [], totalPages: 1 };
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    if (embedded) {
      fetchExpenseTypeData();
    }
  }, [embedded, fetchExpenseTypeData, RefreshTrigger]);
  const handleExpenseSelectMultiple = (expense) => {
    if (!expense) return;

    const exists = selectedExpenseList.some(e => String(e.id) === String(expense.id));

    if (!exists) {
      setSelectedExpenseList(prev => [...prev, expense]);
    }
  };

  const handleRemoveFromList = (item) => {
    const idToRemove = typeof item === "object" ? item.id : item;
    setSelectedExpenseList(prev => prev.filter(expense => String(expense.id) !== String(idToRemove)));
  };

 
  const handleAddNew = () => {
    setExpenseType("");
  };

  const handleSave = async () => {
    if (selectedExpenseList.length === 0) {
      handleToast({
        type: "WARNING",
        message: "Please select expense type"
      });
      return;
    }

    try {
      const payload = selectedExpenseList.map(item => ({
        store: storeId,
        all_department: null,
        all_expense_type: item.isCustom ? null : item.id,
        custom_title: item.isCustom ? item.title : null,
        custom_type: item.isCustom ? "expense_type" : null,
      }));

      const response = await Post("vendorDepartmentServiceData", payload);

      if (response.data.total_skipped > 0) {
        handleToast({
          type: "WARNING",
          message: response.data.skipped[0].message
        });
      } else if (response.data.total_created > 0) {
        handleToast({
          type: "SUCCESS",
          message: "Expense types added successfully!"
        });
      }

      setSelectedExpenseList([]);
      setRefreshTrigger(prev => prev + 1);

    } catch (error) {
      console.error(error);
      const errorData = error.response?.data;
      let errorMessage = "Error adding expense type";

      if (errorData?.total_skipped > 0) {
        errorMessage = errorData.skipped[0].message;
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      }

      handleToast({
        type: "ERROR",
        message: errorMessage
      });
    }
  };

  const handleAddAllDefaults = async () => {
    setLoading(true);
    try {
      const options = await fetchAllPaginatedOptions(GetPaginatedData, "allExpenseTypeData");
      const existingIds = new Set(expenseTypeRows.map((item) => String(item.all_expense_type)).filter(Boolean));
      const payload = options
        .filter((item) => !existingIds.has(String(item.id)))
        .map((item) => ({ store: storeId, all_department: null, all_expense_type: item.id }));
      if (!payload.length) {
        handleToast({ type: "INFO", message: "All default expense types are already added." });
        return;
      }
      const response = await Post("vendorDepartmentServiceData", payload);
      handleToast({ type: "SUCCESS", message: getBulkResultMessage(response, "default expense types") });
      setSelectedExpenseList([]);
      setRefreshTrigger((current) => current + 1);
      if (embedded) await fetchExpenseTypeData();
    } catch (error) {
      handleToast({ type: "ERROR", message: error.response?.data?.message || "Unable to add all default expense types." });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await confirmDelete();
    if (!result.isConfirmed) return;

    try {
      await Delete("vendorDepartmentServiceData", id);
      setRefreshTrigger((prev) => prev + 1);

      handleToast({
        type: "SUCCESS",
        message: "Expense type deleted successfully!",
      });
    } catch (error) {
      console.error("Error deleting expense type:", error);
      handleToast({
        type: "ERROR",
        message: "Error deleting expense type: " + (error.response?.data?.message || error.message),
      });
    }
  };

  const columns = [{ header: "Expense Type", field: "title" }];

  return (
    <>
      <div className={embedded ? "settings-embedded-tool-panel" : "responsive-box"}>
        <div className="">
          <div className="input-container">
            <div className="input-row">
              <div className="form-group product-field">
                <PaginatedSearchSelect
                  api="allExpenseTypeData"
                  GetPaginatedData={GetPaginatedData}
                  getOptionLabel={(opt) => opt.title || ""}
                  onSelect={handleExpenseSelectMultiple}
                  placeholder="Select Expense Type"
                  pageSize={10}
                  allowCustom
                  customOptionLabel={(value) => `Add custom expense type "${value}"`}
                  filterFn={(opt) =>
                    !selectedExpenseList.some(item => String(item.id) === String(opt.id)) &&
                    !expenseTypeRows.some(item => String(item.all_expense_type) === String(opt.id))
                  }
                />
              </div>
              <CustomButton
                text="Add Expense Type"
                icon={expenseTypeIcon}
                iconPosition="right"
                onClick={handleSave}
              />
              <CustomButton text="Add All Defaults" onClick={handleAddAllDefaults} />
            </div>
          </div>

          <ChipList
            items={selectedExpenseList}
            onRemove={handleRemoveFromList}
            displayField="title"
            keyField="id"
          />
        </div>
      </div>
      <div className={embedded ? "settings-embedded-tool-panel settings-embedded-table-panel" : "responsive-box"}>
        {embedded ? (
          <div className="settings-chip-board">
            {expenseTypeRows.length > 0 ? (
              expenseTypeRows.map((expense) => (
                <div className="settings-config-chip" key={expense.id}>
                  <span>{expense.title || "N/A"}</span>
                  <button
                    type="button"
                    className="settings-chip-delete"
                    aria-label={`Delete ${expense.title || "expense type"}`}
                    onClick={() => handleDelete(expense.id)}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))
            ) : (
              <div className="settings-empty-state settings-table-empty">
                <strong>No expense types added yet</strong>
                <span>Select expense types above to keep spending categories organized.</span>
              </div>
            )}
          </div>
        ) : (
          <ReusableTable
            fetchData={fetchExpenseTypeData}
            showFooter={true}
            columns={columns}
            showAction={true}
            onDelete={handleDelete}
            loading={loading}
            refreshKey={RefreshTrigger}
          />
        )}
        {!embedded && <ToastContainer />}
      </div>
      <ReusableModal
        title="Add Expense Type"
        show={showModal}
        handleClose={() => setShowModal(false)}
        width="600px"
        height="550px"
      >

        <div className="product-form">
          <div className="form-group product-field">
            <PaginatedSearchSelect
              api="vendorDepartmentServiceData"
              GetPaginatedData={GetPaginatedData}
              getOptionLabel={(opt) => opt.title || ""}
              onSelect={handleExpenseSelectMultiple}
              placeholder="Select Expense Type"
              pageSize={10}
              filterFn={(opt) =>
                !selectedExpenseList.some(item => String(item.id) === String(opt.id)) &&
                !expenseTypeRows.some(item => String(item.all_expense_type) === String(opt.id))
              }
            />
          </div>
        </div>

        {/* Selected Expense Types */}
        <div className="selected-uoms-section" style={{ marginTop: "20px" }}>
          <div className="uom-list">
            {selectedExpenseList.map((expense) => (
              <div className="uom-card" key={expense.id}>
                <span className="uom-title">{expense.title}</span>
                <img
                  src={selectIcon}
                  alt="Remove"
                  className="remove-uom-icon"
                  onClick={() => handleRemoveFromList(expense.id)}
                />
              </div>
            ))}

          </div>

        </div>

        <div className="submit-section">

          <button
            type="button"
            className="form-btn submit-btn"
            onClick={handleSave}
          >
            Add
          </button>

        </div>

      </ReusableModal>
    </>

  );
}

export default ManageExpenseType;
